import { useState, useCallback } from 'react';
import { Activity, Search } from 'lucide-react';
import { Container, PageHeader, Toolbar } from '@vnetwork/ui/layout';
import { DataTable, EmptyState, Pagination } from '@vnetwork/ui/data';
import { Skeleton, Input } from '@vnetwork/ui/primitives';
import { useAuditLogs, type AuditLog, type AuditLogParams } from '@/api/audit';
import { useAuditColumns } from './audit-log-columns';
import {
  AuditLogFilterBar,
  AUDIT_FILTER_DEFAULTS,
  type AuditFilterValues,
} from './AuditLogFilterBar';
import { AuditLogDetailDialog } from './AuditLogDetailDialog';

const PAGE_SIZE = 20;

function SkeletonRows() {
  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-2)' }}>
      {Array.from({ length: 8 }).map((_, i) => (
        <Skeleton key={i} style={{ height: 44 }} />
      ))}
    </div>
  );
}

function ErrorState() {
  return (
    <div
      style={{
        padding: 'var(--vn-space-8)',
        textAlign: 'center',
        color: 'var(--vn-danger-text)',
        fontSize: 'var(--vn-font-size-md)',
      }}
    >
      Failed to load activity logs. Please try again.
    </div>
  );
}

export function ActivityPage() {
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<AuditFilterValues>(AUDIT_FILTER_DEFAULTS);
  const [detailLog, setDetailLog] = useState<AuditLog | null>(null);

  const handleFilterChange = useCallback((patch: Partial<AuditFilterValues>) => {
    setFilters((prev) => ({ ...prev, ...patch }));
    setPage(1);
  }, []);

  const handleClear = useCallback(() => {
    setFilters(AUDIT_FILTER_DEFAULTS);
    setPage(1);
  }, []);

  const hasFilters = Object.values(filters).some(Boolean);

  const params: AuditLogParams = {
    page,
    per_page: PAGE_SIZE,
    resource_name: filters.resourceName.trim() || undefined,
    action: filters.action || undefined,
    status: (filters.status as AuditLogParams['status']) || undefined,
    resource_type: filters.resourceType || undefined,
    start_date: filters.startDate || undefined,
    end_date: filters.endDate || undefined,
  };

  const { data, isLoading, isError } = useAuditLogs(params);
  const rows = data?.data ?? [];
  const total = data?.pagination?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleDetail = useCallback((log: AuditLog) => setDetailLog(log), []);
  const columns = useAuditColumns(handleDetail);

  const showEmpty = !isLoading && !isError && total === 0 && !hasFilters;
  const showTable = !isLoading && !isError && (total > 0 || hasFilters);

  return (
    <>
      <Container size="xl">
        <PageHeader
          icon={<Activity size={22} />}
          title="Activity log"
          description="Track all organization changes — resource mutations, team actions, and permission updates."
        />

        <Toolbar
          start={
            <Input
              placeholder="Search by resource name"
              startSlot={<Search size={14} />}
              value={filters.resourceName}
              onChange={(e) => handleFilterChange({ resourceName: e.target.value })}
              style={{ maxWidth: 240 }}
            />
          }
        />

        {/* Filter selects on their own wrapping row so they don't overflow on narrow viewports. */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-2)', marginBottom: 'var(--vn-space-3)' }}>
          <AuditLogFilterBar
            values={filters}
            onChange={handleFilterChange}
            onClear={handleClear}
            hasFilters={hasFilters}
          />
        </div>

        {isLoading && <SkeletonRows />}
        {isError && <ErrorState />}

        {showEmpty && (
          <EmptyState
            icon={<Activity size={28} />}
            title="No activity yet"
            description="Actions taken in your organization will appear here."
          />
        )}

        {showTable && (
          <>
            <DataTable
              columns={columns}
              data={rows}
              getRowId={(r, i) => `${r.resource_id}-${r.created_at}-${i}`}
              emptyState="No logs match your filters."
              footer={
                rows.length > 0
                  ? `Showing ${(page - 1) * PAGE_SIZE + 1}–${(page - 1) * PAGE_SIZE + rows.length} of ${total}`
                  : undefined
              }
            />
            {pageCount > 1 && (
              <div style={{ marginTop: 'var(--vn-space-4)' }}>
                <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
              </div>
            )}
          </>
        )}
      </Container>

      <AuditLogDetailDialog log={detailLog} onClose={() => setDetailLog(null)} />
    </>
  );
}

export default ActivityPage;
